import { IconSpinner } from "@/assets/icons/geist/IconSpinner";
import { OnaWordmark } from "@/assets/icons/OnaWordmark";
import onaGradientBg from "@/assets/ona-gradient.webp";
import { DesktopSignin } from "@/components/login/DesktopSignin";
import { LoginFooter } from "@/components/login/LoginFooter";
import { Text } from "@/components/podkit/typography/Text";
import { useLocalRunner } from "@/hooks/use-local-runner";
import { useListLoginProviders } from "@/queries/account-queries";
import { SSOLogin } from "@/routes/login/LoginPage";
import FreeCreditsPromotionBanner from "@/routes/ona-onboarding/FreeCreditsPromotionBanner";
import { useState } from "react";

type OnaOnboardingLayoutProps = {
    returnTo: string;
    inviteId?: string;
};

export function OnaOnboardingLayout({ returnTo, inviteId }: OnaOnboardingLayoutProps) {
    const [showContinueWithSSO, setShowContinueWithSSO] = useState(false);

    return (
        <div
            data-testid="ona-onboarding-login-page"
            className="flex h-full min-h-screen w-full flex-col items-center justify-between bg-cover bg-center bg-no-repeat p-0 text-gray-900 md:p-6"
            style={{
                backgroundImage: `url(${onaGradientBg})`,
            }}
        >
            <div className="flex w-128 max-w-full flex-1 flex-col items-center justify-center px-4 md:px-0">
                {!showContinueWithSSO && (
                    <FreeCreditsPromotionBanner className="w-full rounded-2xl text-md dark:bg-surface-glass lg:text-md" />
                )}
                <div className="mx-8 mb-8 mt-4 flex w-full flex-col items-center rounded-2xl bg-white px-6 pb-8 shadow-2xl dark:bg-surface-glass dark:text-white">
                    {/* Ona Branding */}
                    <OnaWordmark data-testid="ona-wordmark" className="m-2 mb-4 mt-14 h-10 w-auto" />
                    <Text className="mb-8 text-center text-inherit opacity-60">
                        What do you want to get done today?
                    </Text>

                    <div className="flex w-full flex-col items-center space-y-2">
                        <LoginArea inviteId={inviteId} returnTo={returnTo} onSSOViewChange={setShowContinueWithSSO} />
                    </div>
                </div>
            </div>

            {/* Footer at bottom of page */}
            <div className="mb-5 w-96 max-w-full text-center">
                <LoginFooter className="text-sm text-white" linkClassName="text-sm text-white underline" />
            </div>
        </div>
    );
}

function LoginArea({
    inviteId,
    returnTo,
    onSSOViewChange,
}: {
    inviteId?: string;
    returnTo: string;
    onSSOViewChange: (show: boolean) => void;
}) {
    const { isPending: isPendingProviders } = useListLoginProviders(inviteId);
    const localRunner = useLocalRunner();

    if (inviteId && isPendingProviders) {
        return (
            <p className="mb-6 flex items-center space-x-2 opacity-80">
                <IconSpinner size="lg" className="animate-spin" />
                <span>Loading login options…</span>
            </p>
        );
    }

    return (
        <>
            {localRunner.status && <DesktopSignin />}
            {!localRunner.status && (
                <SSOLogin
                    inviteId={inviteId}
                    returnTo={returnTo}
                    className="w-full"
                    hideLoginToClassic
                    newLoginLayout
                    onSSOViewChange={onSSOViewChange}
                />
            )}
        </>
    );
}
